<template>
  <div class="login-container">
    <Toast />
    <div class="login-card">
      <div class="login-header">
        <div class="logo-container">
          <img src="/assets/images/logo.png" alt="MFG logo" class="logo" />
        </div>
        <h2 class="login-title">Welcome Back</h2>
        <p class="login-subtitle">Assalamualaikum, silahkan login dahulu</p>
      </div>

      <form @submit.prevent="doLogin()" class="login-form">
        <div class="p-field">
          <label for="email" class="form-label">Email</label>
          <InputText
            id="email"
            type="email"
            v-model="form.email"
            placeholder="Type your email here"
            :class="{ 'p-invalid': errors.email }"
            autocomplete="email"
            aria-describedby="email-error"
            aria-required="true"
            aria-invalid="!!errors.email"
            @blur="validateEmail(form.email)"
            @input="errors.email = ''"
            class="w-100"
          />
          <small
            id="email-error"
            class="p-error"
            v-if="errors.email"
            role="alert"
          >
            {{ errors.email }}
          </small>
        </div>

        <div class="p-field">
          <label for="password" class="form-label">Password</label>
          <Password
            id="password"
            v-model="form.password"
            placeholder="Type your password here"
            :feedback="false"
            toggleMask
            :class="{ 'p-invalid': errors.password }"
            autocomplete="current-password"
            aria-describedby="password-error"
            aria-required="true"
            aria-invalid="!!errors.password"
            @blur="validatePassword(form.password)"
            @input="errors.password = ''"
            class="w-100"
          />
          <small
            id="password-error"
            class="p-error"
            v-if="errors.password"
            role="alert"
          >
            {{ errors.password }}
          </small>
        </div>

        <div class="p-field" v-if="errors.login">
          <small class="p-error" role="alert">{{ errors.login }}</small>
        </div>

        <div class="p-field">
          <Button
            type="submit"
            :label="loading.loginButton ? 'Logging in...' : 'Login'"
            icon="pi pi-sign-in"
            :loading="loading.loginButton"
            :disabled="!isFormValid || loading.loginButton"
            class="p-button-primary login-button w-100"
          />
        </div>

        <div class="p-field text-center">
          <a
            href="#"
            class="forgot-password-link"
            @click.prevent="showForgotPasswordDialog"
          >
            Forgot password?
          </a>
        </div>
      </form>
    </div>

    <Dialog
      :visible.sync="forgotPasswordDialog"
      :style="{ width: '450px' }"
      header="Forgot Password"
      :modal="true"
    >
      <div class="forgot-password-content">
        <i
          class="pi pi-info-circle"
          style="font-size: 2rem; color: #667eea"
        ></i>
        <p class="forgot-password-message">
          Please contact the IT Administrator
        </p>
      </div>
      <template #footer>
        <Button
          label="OK"
          icon="pi pi-check"
          class="p-button-primary"
          @click="forgotPasswordDialog = false"
        />
      </template>
    </Dialog>

    <div class="login-footer">
      <p class="copyright-text">
        © {{ currentYear }} Putra Muda Mandiri. All rights reserved.
      </p>
      <p class="version-text">Version {{ appVersion }}</p>
    </div>
  </div>
</template>

<script>
// Constants
const RESTRICTED_ROLES = ['CASHIER', 'STOCK_OPNAME']
const MIN_PASSWORD_LENGTH = 6
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  data() {
    return {
      form: {
        email: '',
        password: ''
      },
      errors: {
        email: '',
        password: '',
        login: ''
      },
      loading: {
        loginButton: false
      },
      forgotPasswordDialog: false
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    appVersion() {
      return process.env.VUE_APP_VERSION || '2.0.0'
    },
    isEmailValid() {
      return this.form.email && EMAIL_REGEX.test(this.form.email)
    },
    isPasswordValid() {
      return (
        this.form.password && this.form.password.length >= MIN_PASSWORD_LENGTH
      )
    },
    isFormValid() {
      return (
        this.isEmailValid && this.isPasswordValid && !this.loading.loginButton
      )
    },
    hasErrors() {
      return !!(this.errors.email || this.errors.password || this.errors.login)
    }
  },
  watch: {
    'form.email'(value) {
      if (value) {
        this.validateEmail(value)
      }
    },
    'form.password'(value) {
      if (value) {
        this.validatePassword(value)
      }
    }
  },
  methods: {
    /**
     * Validates email format
     * @param {string} value - Email value to validate
     */
    validateEmail(value) {
      if (!value || value.length === 0) {
        this.errors.email = 'Email is required'
      } else if (!EMAIL_REGEX.test(value)) {
        this.errors.email = 'Please enter a valid email address'
      } else {
        this.errors.email = ''
      }
    },

    /**
     * Validates password (simple validation for login)
     * @param {string} value - Password value to validate
     */
    validatePassword(value) {
      if (!value || value.length === 0) {
        this.errors.password = 'Password is required'
      } else if (value.length < MIN_PASSWORD_LENGTH) {
        this.errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
      } else {
        this.errors.password = ''
      }
    },

    /**
     * Handles login errors and displays appropriate messages
     * @param {Error} error - Error object from login attempt
     */
    handleLoginError(error) {
      // Clear previous login error
      this.errors.login = ''

      if (!error.response) {
        // Network error
        this.errors.login = 'Network error. Please check your connection.'
        this.showToast('error', 'Network Error', 'Unable to connect to server')
        return
      }

      const status = error.response.status
      const message =
        (error.response.data && error.response.data.message) || 'Login failed'

      switch (status) {
        case 401:
          this.errors.login = 'Invalid email or password'
          this.showToast(
            'error',
            'Authentication Failed',
            'Invalid email or password'
          )
          break
        case 403:
          this.errors.login = 'Access denied'
          this.showToast(
            'error',
            'Access Denied',
            'You do not have permission to login'
          )
          break
        case 429:
          this.errors.login = 'Too many login attempts. Please try again later.'
          this.showToast(
            'warn',
            'Rate Limit',
            'Too many login attempts. Please wait a moment.'
          )
          break
        case 500:
          this.errors.login = 'Server error. Please try again later.'
          this.showToast(
            'error',
            'Server Error',
            'An error occurred on the server'
          )
          break
        default:
          this.errors.login = message
          this.showToast('error', 'Error', message)
      }

      // Clear password on error for security
      this.form.password = ''
    },

    /**
     * Shows forgot password dialog
     */
    showForgotPasswordDialog() {
      this.forgotPasswordDialog = true
    },

    /**
     * Performs login action
     */
    async doLogin() {
      // Validate before submission
      if (!this.isFormValid) {
        if (!this.isEmailValid) {
          this.validateEmail(this.form.email)
        }
        if (!this.isPasswordValid) {
          this.validatePassword(this.form.password)
        }
        this.showToast(
          'warn',
          'Validation',
          'Please fill in all required fields correctly'
        )
        return
      }

      // Clear previous errors
      this.errors.login = ''
      this.loading.loginButton = true

      try {
        const response = await this.$store.dispatch('login', {
          user: this.form
        })
        this.loading.loginButton = false

        if (response.status === 200) {
          const userRole = response.data.data.user_info.role

          // Check for restricted roles
          if (RESTRICTED_ROLES.includes(userRole)) {
            this.errors.login = `Role '${userRole}' is not allowed to login from this interface`
            this.showToast(
              'error',
              'Access Denied',
              `Role '${userRole}' is not allowed to login`
            )
            // Clear form
            this.form.password = ''
            return
          }

          // Success - navigate to dashboard
          this.$router.push({ name: 'dashboard' })
        } else {
          this.errors.login = 'Login failed. Please try again.'
          this.showToast('error', 'Error', 'Login failed')
          this.form.password = ''
        }
      } catch (error) {
        this.loading.loginButton = false
        this.handleLoginError(error)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.login-container {
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  padding-bottom: 80px;
}

.login-card {
  width: 100%;
  max-width: 450px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 2.5rem;
  animation: fadeInUp 0.5s ease-out;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo-container {
  margin-bottom: 1.5rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 1rem;
  display: inline-block;
}

.logo {
  max-height: 120px;
  width: auto;
  object-fit: contain;
  display: block;
}

.login-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.login-subtitle {
  font-size: 0.95rem;
  color: #718096;
  margin: 0;
}

.login-form {
  .p-field {
    margin-bottom: 1.5rem;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  .form-label {
    display: block;
    font-weight: 500;
    color: #2d3748;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  .login-button {
    margin-top: 0.5rem;
    padding: 0.75rem;
    font-size: 1rem;
    font-weight: 500;
  }

  .forgot-password-link {
    color: #4a5568;
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s ease;
    cursor: pointer;

    &:hover {
      color: #2d3748;
      text-decoration: underline;
    }
  }
}

.forgot-password-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem 0;
}

.forgot-password-message {
  margin: 1rem 0 0 0;
  font-size: 1rem;
  color: #2d3748;
  line-height: 1.5;
}

.w-100 {
  width: 100%;
}

.text-center {
  text-align: center;
}

.login-footer {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  padding: 0 20px;
}

.copyright-text {
  font-size: 0.85rem;
  color: #718096;
  margin: 0.25rem 0;
}

.version-text {
  font-size: 0.75rem;
  color: #a0aec0;
  margin: 0.25rem 0;
}

// Responsive design
@media screen and (max-width: 576px) {
  .login-container {
    padding: 15px;
  }

  .login-card {
    padding: 1.5rem;
    border-radius: 8px;
  }

  .login-title {
    font-size: 1.5rem;
  }

  .logo {
    max-height: 100px;
  }

  .logo-container {
    padding: 0.75rem;
  }

  .login-footer {
    bottom: 15px;
    padding: 0 15px;
  }

  .copyright-text {
    font-size: 0.8rem;
  }

  .version-text {
    font-size: 0.7rem;
  }
}
</style>
